<template>
  <div>
    <div class="d-flex justify-space-between align-center px-1">
      <AppHeading>Proofs</AppHeading>
      <CanceledSwitch
        v-if="hasCanceled"
        v-model="showCanceled"
        section-name="Proofs"
      />
    </div>
    <div class="pa-1">
      <VExpansionPanels
        v-if="filteredMainProofs.length"
        v-model="openPanels"
        multiple
      >
        <ProofExpansionPanel
          v-for="proof in filteredMainProofs"
          v-show="!isFiltering || isProofVisible(proof.proofId)"
          :key="proof.id"
          :class="{
            'search-highlight': highlightProofId === proof.proofId
          }"
          :proof="proof"
          :add-ons="addOns(proof)"
          :filter-proof-ids="filterProofIds"
        />
      </VExpansionPanels>
      <div v-else class="text-body-2 text--secondary">None</div>
    </div>
  </div>
</template>

<script lang="ts">
import { sortBy } from 'lodash-es'
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import AppHeading from '@/components/AppHeading.vue'
import CanceledSwitch from '@/components/CanceledSwitch.vue'
import { recentOpenPanels } from '@/components/mixins/panelUtils'
import { isMain, sortedAddOnsByProjectId } from '@/components/mixins/proofUtils'
import ProofExpansionPanel from '@/components/ProofExpansionPanel.vue'
import { Proof } from '@/generated'

export default defineComponent({
  components: {
    AppHeading,
    CanceledSwitch,
    ProofExpansionPanel
  },
  props: {
    proofs: {
      type: Array as PropType<Proof[]>,
      required: true
    },
    highlightProofId: {
      type: String as PropType<string | null>,
      default: null
    },
    filterProofIds: {
      type: Array as PropType<string[] | null>,
      default: null
    }
  },
  setup(props) {
    const openPanels = recentOpenPanels(
      props.proofs.map((proof: Proof) => proof.lastModifiedDate as Date),
      'Proof'
    )

    const addOnsByProjectId = computed(() =>
      sortedAddOnsByProjectId(props.proofs)
    )
    const showCanceled = ref(false)
    const mainProofs = computed(() =>
      sortBy(
        props.proofs.filter(isMain),
        (proof: Proof) => -(proof.lastModifiedDate as Date).getTime()
      )
    )
    const hasCanceled = computed(() =>
      mainProofs.value.some((proof) => proof.status === 'Cancelled')
    )
    const filteredMainProofs = computed(() =>
      mainProofs.value.filter(
        (proof) => showCanceled.value || proof.status !== 'Cancelled'
      )
    )

    // When a highlight proof is set, force that proof's panel open
    watch(
      () => props.highlightProofId,
      (proofId) => {
        if (!proofId) return
        const idx = filteredMainProofs.value.findIndex(
          (p) => p.proofId === proofId
        )
        if (idx >= 0) {
          openPanels.value = [idx]
        }
      },
      { immediate: true }
    )

    const isFiltering = computed(() => props.filterProofIds !== null)
    const filterSet = computed(() => new Set(props.filterProofIds || []))

    // When filter activates, force filtered proofs open
    watch(
      () => props.filterProofIds,
      (ids) => {
        if (!ids || !ids.length) return
        const indices = ids
          .map((id) =>
            filteredMainProofs.value.findIndex((p) => p.proofId === id)
          )
          .filter((i) => i >= 0)
        if (indices.length) {
          const merged = new Set([...indices, ...openPanels.value])
          openPanels.value = [...merged]
        }
      }
    )

    return {
      isFiltering,
      isProofVisible(proofId: string) {
        return filterSet.value.has(proofId)
      },
      isProofFiltered(proofId: string) {
        return isFiltering.value && filterSet.value.has(proofId)
      },
      hasCanceled,
      mainProofs,
      filteredMainProofs,
      addOns: (proof: Proof): Proof[] =>
        (proof.project?.projectId &&
          addOnsByProjectId.value[proof.project?.projectId]) ||
        [],
      openPanels,
      showCanceled
    }
  }
})
</script>

<style scoped lang="scss">
@import '~vuetify/src/styles/styles.sass';

::v-deep .v-list-item__title {
  // Make title smaller to reduce truncation
  font-size: $font-size-root;
}
</style>
